import { ListHeading } from "@/components/shared/list-header";
import { Button, Skeleton, View, Card, CardContent } from "@/components/ui";
import type { Icon } from "@/components/shared/icons";
import { VisitCard } from "@/components/shared/visits/visit-card";
import { IfElse } from "@/components/shared/if-else";
import NoVisitsBike from "@/assets/images/3d-illus/no-visit-bike.png";
import { EmptyCard } from "@/components/shared/empty-card";
import { useVisitAction } from "@/containers/visits/use-visit-action";
import type { useGetVisitsQuery } from "@/gqlhooks/visit/useGetVisitsQuery";

function VisitCardSkeleton() {
  return (
    <Card radius="xl">
      <View className="flex flex-col">
        {/* Top section with service type and price */}
        <View className="p-4">
          <View className="mb-2 flex flex-row justify-between">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-16" />
          </View>
          {/* Schedule */}
          <Skeleton className="mb-1 h-4 w-48" />
          {/* Address */}
          <Skeleton className="h-4 w-40" />
        </View>
        {/* Divider */}
        <View className="native:border-b-border native:border-solid mx-4 border-b border-dashed border-border-medium" />
        {/* Bottom section with worker and status */}
        <View className="flex flex-row justify-between gap-2 px-4 py-2">
          <View className="flex max-w-[50%] flex-row items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-4 w-20" />
          </View>
          <Skeleton className="h-6 w-16 rounded-full" />
        </View>
      </View>
      {/* Action buttons skeleton */}
      <CardContent className="gap-4 pt-0">
        <View className="mt-2 flex flex-row justify-end gap-2">
          <Skeleton className="h-10 w-24" />
          <Skeleton className="h-10 w-24" />
        </View>
      </CardContent>
    </Card>
  );
}

type VisitListProps = {
  query: ReturnType<typeof useGetVisitsQuery>;
  title: string;
  subtitle?: string;
  icon: Icon;
};

export function VisitList({ query, title, subtitle, icon }: VisitListProps) {
  const {
    handleViewDetail,
    handleRebook,
    handleSetSchedule,
    handleRateVisit,
    handleReportVisit,
  } = useVisitAction();
  const onLoadMore = async () => {
    if (!query?.canLoadMore || query.loading) return;
    if (query.fetchMore && query.variables && "filters" in query.variables) {
      const filters = {
        ...(query.variables as { filters?: Record<string, unknown> }).filters,
        offset: query.list.length,
      };
      await query.fetchMore({
        variables: {
          filters,
        },
      });
    }
  };
  const visits = query.list;
  const shouldShowLoadMore = query.canLoadMore;
  return (
    <View className="flex flex-col gap-4">
      <ListHeading icon={icon} title={title} subtitle={subtitle || ""} />
      {query.loading && visits.length === 0 ? (
        <View className="flex flex-col gap-4">
          <VisitCardSkeleton />
          <VisitCardSkeleton />
          <VisitCardSkeleton />
          <VisitCardSkeleton />
        </View>
      ) : (
        <IfElse
          if={!!visits?.length}
          else={
            <EmptyCard
              src={NoVisitsBike}
              title="You don't have any upcoming visits"
              desc="Your scheduled services will appear here once booked."
            />
          }
        >
          <View className="flex flex-col gap-4 md:p-0">
            {visits.map((visit) => (
              <VisitCard
                key={visit.id}
                visitData={visit}
                handleViewDetail={handleViewDetail}
                handleRateVisit={handleRateVisit}
                handleRebook={handleRebook}
                handleSetSchedule={handleSetSchedule}
                handleReportVisit={handleReportVisit}
              />
            ))}
          </View>
        </IfElse>
      )}

      <IfElse if={shouldShowLoadMore}>
        <Button
          variant="tertiary"
          color="CTA2"
          iconColor="CTA2"
          className="w-full"
          loading={query.loading}
          disabled={query.loading}
          onClick={onLoadMore}
          iconName="arrowCounterClockwise"
          iconAlignment="end"
          children="Show More"
        />
      </IfElse>
    </View>
  );
}
